import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ServiceGrid from "@/pages/marketplace/ServiceGrid";
import PurchaseModal, { computeVisitDelta, computeUnitPrice } from "@/pages/marketplace/PurchaseModal";
import RecapTable from "@/pages/marketplace/RecapTable";
import QuotesTable from "@/pages/marketplace/QuotesTable";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DemoBanner, HelpTip } from "@/components/DemoHelpers";
import OperatorPageTip from "@/components/OperatorPageTip";

export default function Marketplace() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [deleteQuote, setDeleteQuote] = useState(null);
  const [recap, setRecap] = useState([]);
  const [plafond, setPlafond] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [search, setSearch] = useState("");
  // Iter208.62 — Filtro categoria condiviso con l'admin (service_categories)
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  // Tab controllata: consente di switchare programmaticamente dopo un acquisto riuscito.
  const [activeTab, setActiveTab] = useState("recap");
  const [serviceMode, setServiceMode] = useState(null);

  // PurchaseModal driver: quale servizio aprire + eventuale seed dello stato interno
  const [selected, setSelected] = useState(null);
  const [openIntent, setOpenIntent] = useState(null);

  const filteredServices = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s) => {
      if (categoryFilter && (s.category || "") !== categoryFilter) return false;
      if (!q) return true;
      const hay = [s.name, s.description, s.category, ...(s.keywords || [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [services, search, categoryFilter]);

  const loadQuotes = () => api.get("/quotes").then((r) => setQuotes(r.data || [])).catch(() => {});
  const loadProperties = () => api.get("/properties").then((r) => setProperties(r.data || []));
  const loadRecap = () =>
    api
      .get("/practices/monthly-recap")
      .then((r) => {
        setRecap(r.data || []);
        if (r.data?.length > 0) setExpandedMonth(`${r.data[0].year}-${String(r.data[0].month).padStart(2, "0")}`);
      })
      .catch(() => setRecap([]));

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/service-categories").then((r) => setCategories(r.data || [])).catch(() => {});
    if (user.role === "professional") {
      loadProperties();
      loadQuotes();
      loadRecap();
      // Carica il plafond dell'utente (solo per pro parent: i sub-user non lo vedono).
      // /plafonds è accessibile a tutti gli autenticati; filtriamo lato client.
      if (user.plafond_id && !user.parent_user_id) {
        api.get("/plafonds")
          .then((r) => setPlafond((r.data || []).find((p) => p._id === user.plafond_id) || null))
          .catch(() => setPlafond(null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Deep-link dal ProChatWidget: se URL contiene ?openService=ID apre PurchaseModal
  // direttamente. Il parametro viene rimosso dall'URL dopo l'apertura per non riaprire
  // il modal a ogni re-render.
  useEffect(() => {
    const svcId = searchParams.get("openService");
    if (!svcId || !services.length) return;
    const svc = services.find((s) => (s._id || s.id) === svcId);
    if (svc) {
      // Se arriva anche un propertyId, pre-compila il form del modal
      const propId = searchParams.get("propertyId");
      if (propId) {
        setOpenIntent({ form: { property_id: propId } });
      }
      setSelected(svc);
      // Rimuovi i param dall'URL preservando gli altri
      const next = new URLSearchParams(searchParams);
      next.delete("openService");
      next.delete("propertyId");
      setSearchParams(next, { replace: true });
    }
  }, [services, searchParams, setSearchParams]);

  // Iter208.85 — Deep-link dalle notifiche "Comunicazioni": switcha alla tab
  // Recap così l'utente vede la pratica corrispondente. L'apertura del modal
  // avviene dentro RecapTable, che legge lo stesso query param.
  useEffect(() => {
    if (searchParams.get("open_practice") && user?.role === "professional") {
      setActiveTab("recap");
    }
  }, [searchParams, user]);

  // Iter209dzq — Refresh silenzioso dello storico (stato pratiche aggiornato).
  useEffect(() => {
    if (user?.role !== "professional") return undefined;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      api.get("/practices/monthly-recap").then((r) => setRecap(r.data || [])).catch(() => {});
    };
    const t = setInterval(refresh, 20000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(t); window.removeEventListener("focus", refresh); };
  }, [user]);


  const closeModal = () => {
    if (openIntent?.stage === "info" && openIntent?.intent) {
      setActiveTab(openIntent.intent === "quote" ? "quotes" : "recap");
      setServiceMode(null);
    }
    setSelected(null);
    setOpenIntent(null);
  };

  const openPurchaseDirect = () => setServiceMode("purchase");
  const openQuoteDirect = () => setServiceMode("quote");

  const selectService = (svc) => {
    setOpenIntent(serviceMode ? { stage: "info", intent: serviceMode } : null);
    setSelected(svc);
  };

  const convertQuoteToOrder = (q, svc) => {
    const proBase = computeUnitPrice(svc, computeVisitDelta(svc, [user?._id, user?.parent_user_id], !!q.visit_skipped));
    const markup = Math.max(0, (Number(q.price_excl_vat) || 0) - proBase);
    // Apri PurchaseModal seedandolo direttamente al bivio "payment_choice"
    setOpenIntent({
      stage: "payment_choice",
      visitSkipped: !!q.visit_skipped,
      form: {
        property_id: q.property_id,
        is_shared_with_client: false,
        client_name: "",
        client_email: "",
        client_markup: markup,
        notes: q.notes || "",
        priority: "normal",
      },
    });
    setSelected(svc);
  };

  const confirmDeleteQuote = async () => {
    try {
      await api.delete(`/quotes/${deleteQuote._id}`);
      toast.success("Preventivo eliminato");
      setDeleteQuote(null);
      loadQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const isPro = user.role === "professional";

  return (
    <div>
      <OperatorPageTip
        pageKey="servizi"
        title="Servizi tecnici Housetag"
        body="Vedi il catalogo dei servizi che puoi gestire come operatore. Se vuoi accettare un incarico entra nel dettaglio della pratica dalla Bacheca; qui vedi solo il listino aggiornato."
      />
      <DemoBanner />
      <div className="mb-8">
        <div className="label-eyebrow">Marketplace</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
          Servizi tecnici
          <HelpTip text="Il marketplace ti mostra tutti i servizi tecnici acquistabili configurati dall'admin (APE, catastali, sanatorie...). Il prezzo che vedi è già personalizzato in base alla tua fascia commerciale." />
        </h1>
        <p className="text-muted-foreground mt-2">
          Acquista servizi certificati per te o rivendili al cliente finale con margine
        </p>
      </div>

      {(!isPro || serviceMode) && (
        <div data-testid="services-picker">
          {isPro && (
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setServiceMode(null)}
                className="text-sm text-muted-foreground hover:text-foreground"
                data-testid="services-picker-back-btn"
              >
                ← Torna {serviceMode === "quote" ? "ai preventivi" : "alle pratiche"}
              </button>
              <span className="text-sm font-semibold" data-testid="services-picker-title">
                {serviceMode === "quote" ? "Scegli il servizio per il nuovo preventivo" : "Scegli il servizio per la nuova pratica"}
              </span>
            </div>
          )}
          {categories.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="marketplace-category-filter">
              <button
                type="button"
                onClick={() => setCategoryFilter("")}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
                  (categoryFilter === "" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                }
                data-testid="marketplace-cat-all"
              >
                Tutte
              </button>
              {categories.map((c) => (
                <button
                  key={c._id}
                  type="button"
                  onClick={() => setCategoryFilter(c.slug)}
                  className={
                    "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
                    (categoryFilter === c.slug ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                  }
                  data-testid={`marketplace-cat-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <ServiceGrid
            services={filteredServices}
            user={user}
            search={search}
            onSearchChange={setSearch}
            onSelect={selectService}
          />
        </div>
      )}

      {isPro && !serviceMode && (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="mb-4 flex items-center gap-3">
          <TabsList>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              Preventivi ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="recap" data-testid="tab-recap">
              Pratiche
            </TabsTrigger>
          </TabsList>
          {activeTab === "recap" && (
            <Button size="default" className="ml-auto ht-gradient border-0 text-sm px-4 py-2" onClick={openPurchaseDirect} data-testid="new-practice-btn">
              + Nuova pratica
            </Button>
          )}
          {activeTab === "quotes" && (
            <Button size="default" className="ml-auto ht-gradient border-0 text-sm px-4 py-2" onClick={openQuoteDirect} data-testid="new-quote-btn">
              + Nuovo preventivo
            </Button>
          )}
        </div>

          <TabsContent value="recap">
            <div className="mb-4 text-xs text-muted-foreground">
              Storico dei servizi acquistati raggruppato per mese. Ogni riga mostra lo stato
              (<strong>In lavorazione</strong> o <strong>Completato</strong>); clicca la riga
              per aprire la cartella con i documenti del servizio.
            </div>
            <RecapTable recap={recap} expandedMonth={expandedMonth} onToggleMonth={setExpandedMonth} />
          </TabsContent>

          <TabsContent value="quotes">
            <div className="mb-4 text-xs text-muted-foreground">
              I preventivi vengono conservati per <strong>30 giorni</strong> dalla data di emissione e poi eliminati
              automaticamente.
            </div>
            <QuotesTable
              quotes={quotes}
              services={services}
              onConvertToOrder={convertQuoteToOrder}
              onDelete={setDeleteQuote}
            />
          </TabsContent>
      </Tabs>
      )}

      <PurchaseModal
        selected={selected}
        services={services}
        properties={properties}
        user={user}
        plafond={plafond}
        openIntent={openIntent}
        onClose={closeModal}
        onPropertiesChanged={loadProperties}
        onQuotesChanged={loadQuotes}
        onPurchaseSuccess={() => {
          // Aggiorna il recap e sposta l'utente sulla tab "Storico servizi"
          // (contiene sia i servizi in lavorazione che quelli completati).
          loadRecap();
          setServiceMode(null);
          setActiveTab("recap");
        }}
      />

      <Dialog open={!!deleteQuote} onOpenChange={(v) => !v && setDeleteQuote(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminare il preventivo?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Stai per eliminare il preventivo <strong>{deleteQuote?.service_name}</strong> per{" "}
            {deleteQuote?.property_summary}. L'operazione è definitiva.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteQuote(null)}>
              Annulla
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-quote-confirm"
              onClick={confirmDeleteQuote}
            >
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
